import type { ColumnDef } from "@tanstack/react-table";
import { buildActionColumn, buildTagColumn } from "@/components/Table";
import { compareNullableIsoDates, renderEmptyCellState } from "@/components/Table/utils";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { sanitizeHtmlId } from "@/lib/htmlHelpers";

export type CommitteeStatus = "Active" | "Inactive" | "Planned";

export type CommitteeAssignmentRow = {
  id: string;
  committeeName: string;
  position: string;
  startDate: string;
  endDate: string | null;
  status: CommitteeStatus;
  section?: {
    id: string;
    name: string;
    legacyId?: string | null;
  } | null;
};

const COMMITTEE_HISTORY_STATUS_TAG_TYPES: Record<CommitteeStatus, "success" | "surface" | "information"> = {
  Active: "success",
  Inactive: "surface",
  Planned: "information"
};

interface GetCommitteeHistoryColumnsOptions {
  onViewCommittee?: (row: CommitteeAssignmentRow) => void;
  setMenu?: (menu: TableRowMenu) => void;
}

export function getCommitteeHistoryColumns({
  onViewCommittee,
  setMenu
}: GetCommitteeHistoryColumnsOptions = {}): ColumnDef<CommitteeAssignmentRow, unknown>[] {
  const columns: ColumnDef<CommitteeAssignmentRow, unknown>[] = [
    {
      header: "Committee Name",
      id: "committeeName",
      accessorKey: "committeeName",
      meta: { headerClassName: "hide-header-in-column", sortType: "text" }
    },
    {
      header: "Position",
      id: "position",
      accessorKey: "position",
      meta: { sortType: "text" }
    },
    {
      header: "Start Date",
      id: "startDate",
      accessorKey: "startDate",
      meta: { sortType: "date" },
      cell: ({ row }) => renderEmptyCellState(row.original.startDate ? toFormat(row.original.startDate) : null)
    },
    {
      header: "End Date",
      id: "endDate",
      accessorKey: "endDate",
      meta: { sortType: "date" },
      sortingFn: (rowA, rowB) => compareNullableIsoDates(rowA.original.endDate, rowB.original.endDate, false),
      cell: ({ row }) => renderEmptyCellState(row.original.endDate ? toFormat(row.original.endDate) : null)
    },
    buildTagColumn<CommitteeAssignmentRow>({
      header: "Status",
      accessorKey: "status",
      getTagLabel: (row) => row.status,
      getTagType: (row) => COMMITTEE_HISTORY_STATUS_TAG_TYPES[row.status],
      sortType: "text"
    })
  ];

  if (onViewCommittee && setMenu) {
    columns.push(
      buildActionColumn<CommitteeAssignmentRow>({
        getActions: (row) => [
          {
            text: "View Committee",
            onClick: () => onViewCommittee(row)
          }
        ],
        actionId,
        setMenu,
        getRowId: (row) => sanitizeHtmlId(row.id)
      })
    );
  }

  return columns;
}
